import { useMemo } from 'react';

import { AtlasRouteIds } from '@/components/navigation/constants';
import { routeOptions } from '@/components/navigation/left/constants';
import { ROUTE_FEATURE_FLAGS } from '@/config/route-feature-flags';
import { useAccessControl } from '@/hooks/query-hooks/gaia-api/access-control';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useLocalOnlineMode } from '@/hooks/use-local-online-mode';
import { usePrivilegeAccessControl } from '@/hooks/use-privilege-access-control';

/**
 * Returns the filtered list of nav items that the current user has access to.
 *
 * For flagRestricted items, looks up the feature flag key from ROUTE_FEATURE_FLAGS
 * to determine if the nav item should be visible. Items that are both flagRestricted
 * and accessRestricted must pass both the feature flag check and the entitlement check.
 * Items with privilegeRestricted must also have the current user holding the specified
 * privilege (evaluated against the grantTable via usePrivilegeAccessControl).
 *
 * @returns {NavItemConfig[]}
 */
export const useLeftNavAccess = () => {
  // Get the current facility ID from the URL
  const { agentId } = useCurrentFacility();

  const { data: accessControls } = useAccessControl({ agentId });

  const featureFlags = useFeatureFlags();

  const { data: privilegeAccess } = usePrivilegeAccessControl();
  const isLocalOnline = useLocalOnlineMode();

  // Memoize the result to prevent unnecessary recalculations
  return useMemo(() => {
    // Filter the leftNavItems based on access controls
    return routeOptions.filter((tab) => {
      if (tab.id === AtlasRouteIds.OfflineEscapeHatch) {
        return isLocalOnline;
      }
      if (tab.privilegeRestricted) {
        if (!privilegeAccess?.[tab.privilegeRestricted]) return false;
      }
      if (tab.flagRestricted) {
        // Look up the feature flag key from ROUTE_FEATURE_FLAGS
        const flagKey = ROUTE_FEATURE_FLAGS[tab.id];
        if (!flagKey || featureFlags[flagKey] !== true) return false;
        // If also accessRestricted, fall through to entitlement check
        if (!tab.accessRestricted) return true;
      }
      return accessControls?.some((control) => control.routeId === tab.id) || !tab.accessRestricted;
    });
  }, [accessControls, featureFlags, isLocalOnline, privilegeAccess]);
};
